import React, { useState, useEffect } from 'react';
import LetterLibrary from '../learning/LetterLibrary';

  const LibraryPanel = ({ client, channelName, userType, onClose, onShareResource }) => {
  const [resources, setResources] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [showAlphabet, setShowAlphabet] = useState(false);

  // Catégories disponibles
  const categories = [
    { id: 'all', label: 'Toutes les ressources', icon: '📚' },
    { id: 'cours-niveau-1', label: 'Cours Niveau 1', icon: '🟢' },
    { id: 'cours-niveau-2', label: 'Cours Niveau 2', icon: '🟡' },
    { id: 'cours-niveau-3', label: 'Cours Niveau 3', icon: '🔴' },
    { id: 'education-islamique', label: 'Éducation Islamique', icon: '🌙' },
    { id: 'exercices-prononciation', label: 'Exercices Prononciation', icon: '🎤' },
    { id: 'grammaire-arabe', label: 'Grammaire Arabe', icon: '📝' },
    { id: 'vocabulaire', label: 'Vocabulaire', icon: '📖' },
    { id: 'culture-histoire', label: 'Culture & Histoire', icon: '🏛️' }
  ];

  useEffect(() => {
    loadLibraryResources();
  }, []);

  const loadLibraryResources = async () => {
    try {
      setIsLoading(true);
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/agora/library/resources`);
      //const response = await fetch('/api/agora/library/resources');
      
      if (!response.ok) {
        throw new Error('Erreur chargement bibliothèque');
      }
      
      const data = await response.json();
      setResources(data.resources || []);
      
      console.log('📚 Ressources chargées:', data.resources?.length || 0);
      
    } catch (error) {
      console.error('❌ Erreur chargement bibliothèque:', error);
      alert('Impossible de charger la bibliothèque: ' + error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const shareResource = async (resource) => {
    try {
      console.log('🔄 Partage ressource:', resource);

      // ══════════════════════════════════════════════════
      // CAS : Présentation PowerPoint (.pptx) → TÉLÉCHARGEMENT
      // Le prof la télécharge, l'ouvre dans PowerPoint (diaporama), puis
      // clique « Partager écran ». Lien signé (courte durée) = dissuasion.
      // ══════════════════════════════════════════════════
      if (resource.isPptx) {
        const token = localStorage.getItem('token');
        const resp = await fetch(
          `${process.env.REACT_APP_API_URL}/presentations/${resource.presentationId}/download`,
          { method: 'GET', headers: { 'Authorization': `Bearer ${token}` } }
        );
        if (!resp.ok) throw new Error('Impossible de récupérer le fichier');
        const data = await resp.json();
        if (!data?.url) throw new Error('Lien de téléchargement indisponible');

        // Téléchargement avec un NOM PROPRE : l'attribut <a download> gère l'unicode
        // (arabe) correctement, contrairement au Content-Disposition encodé en %XX.
        const path = (data.url.split('?')[0] || '').toLowerCase();
        const ext = path.endsWith('.ppt') ? 'ppt' : 'pptx';
        const cleanName = (resource.originalName || 'presentation')
          .replace(/[‎‏‪-‮⁦-⁩]/g, '') // marques RTL invisibles
          .replace(/[\\/:*?"<>|]/g, ' ').replace(/\s+/g, ' ').trim() || 'presentation';
        try {
          const fileResp = await fetch(data.url);
          const blob = await fileResp.blob();
          const blobUrl = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = blobUrl;
          a.download = `${cleanName}.${ext}`;
          document.body.appendChild(a);
          a.click();
          a.remove();
          setTimeout(() => URL.revokeObjectURL(blobUrl), 4000);
        } catch (e) {
          // Repli si le fetch cross-origin échoue (CORS) : ouverture directe
          window.open(data.url, '_blank');
        }
        alert(
          `⬇️  « ${resource.originalName} » téléchargé.\n\n` +
          `Pour le montrer à l'élève, 3 gestes :\n` +
          `1.  Ouvre le fichier (double-clic)\n` +
          `2.  Lance le diaporama\n` +
          `      • PowerPoint : onglet Diaporama → À partir du début\n` +
          `      • Keynote : bouton ▶ (ou ⌘⌥P)\n` +
          `3.  Ici, dans le cours : « Partager écran » → « Tout l'écran »\n\n` +
          `💡  PowerPoint : décoche « Utiliser le mode Présentateur » (une seule fois).\n` +
          `      Keynote : rien à faire.`
        );
        onClose();
        return;
      }

      // ══════════════════════════════════════════════════
      // CAS SPÉCIAL : Présentation Keynote (Supabase) — ancien format, en retrait
      // ══════════════════════════════════════════════════
      if (resource.isKeynotePresentation) {
        // 1. Récupérer le token + URL iframe depuis le backend
        const token = localStorage.getItem('token');
        const openResponse = await fetch(
          `${process.env.REACT_APP_API_URL}/presentations/${resource.presentationId}/open`,
          {
            method: 'GET',
            credentials: 'include',
            headers: {
              'Authorization': `Bearer ${token}`,
            },
          }
        );

        if (!openResponse.ok) {
          throw new Error('Impossible d\'ouvrir la présentation');
        }

        const openData = await openResponse.json();
        // openData = { presentation, iframe_url }

        const apiUrl = process.env.REACT_APP_API_URL || '';
        const fullIframeUrl = `${apiUrl}${openData.iframe_url}`;

        // 2. Envoyer un message Agora "keynote_presentation" pour que l'élève l'ouvre aussi
        const keynoteMessage = {
          type: 'keynote_presentation',
          presentationId: resource.presentationId,
          nom: resource.originalName,
          lettre: resource.lettre,
          iframe_url: fullIframeUrl,
          action: 'open',
          id: Date.now() + Math.random(),
          timestamp: new Date().toLocaleTimeString(),
        };

        const encoder = new TextEncoder();
        await client.sendStreamMessage(encoder.encode(JSON.stringify(keynoteMessage)));
        console.log('✅ Message keynote envoyé via Agora');

        // 3. Ouvrir aussi côté prof via event
        if (typeof window !== 'undefined') {
          window.dispatchEvent(new CustomEvent('agora-keynote-message', {
            detail: keynoteMessage
          }));
        }

        alert(`✅ Présentation "${resource.originalName}" affichée aux élèves`);
        onClose();
        return;
      }

      // ══════════════════════════════════════════════════
      // CAS NORMAL : Ressource locale (PDF, MP3, image...)
      // ══════════════════════════════════════════════════
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/agora/library/share`, {
      //const response = await fetch('/api/agora/library/share', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          category: resource.category,
          filename: resource.fileName,
          channelName: channelName,
          userType: userType
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Erreur partage ressource');
      }

      const result = await response.json();
      
      // ✅ MODIFICATION PHASE 1 : Envoi de 2 messages
      
      // Message 1: Document classique (pour historique dans la liste)
      const documentMessage = {
        type: 'document',
        ...result.resource,
        id: Date.now() + Math.random(),
      };

      const encoder = new TextEncoder();
      await client.sendStreamMessage(encoder.encode(JSON.stringify(documentMessage)));

      console.log('✅ Message 1 envoyé (historique)');

      // Message 2: AUTO-DISPLAY pour ouverture automatique chez l'élève
      const autoDisplayMessage = {
        type: 'auto_display_document',
        document: {
          ...documentMessage,
          timestamp: new Date().toLocaleTimeString()
        },
        action: 'open'
      };

      console.log('📤 Envoi message 2 (auto-display):', autoDisplayMessage);
      await client.sendStreamMessage(encoder.encode(JSON.stringify(autoDisplayMessage)));
      // ✅ NOUVEAU: Ouvrir aussi le document pour le prof
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('agora-document-message', {
          detail: autoDisplayMessage
        }));
      }
      console.log('✅ Message 2 envoyé (auto-display)');

      // Notifier le parent
      onShareResource({
        ...documentMessage,
        isOwn: true
      });

      alert(`✅ Ressource "${resource.fileName}" partagée et affichée automatiquement aux élèves !`);
      onClose();

    } catch (error) {
      console.error('❌ Erreur partage:', error);
      alert('Impossible de partager la ressource: ' + error.message);
    }
  };

  // Filtrage des ressources
  const filteredResources = resources.filter(resource => {
    const matchesCategory = selectedCategory === 'all' || resource.category === selectedCategory;
    const matchesSearch = resource.fileName.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const getFileIcon = (fileType) => {
    if (fileType?.includes('pdf')) return '📄';
    if (fileType?.includes('image')) return '🖼️';
    if (fileType?.includes('audio')) return '🎵';
    if (fileType?.includes('text')) return '📋';
    return '📎';
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  };

  return (
    <div style={{
      position: 'fixed',
      top: '0',
      left: '0',
      width: '100%',
      height: '100%',
      backgroundColor: 'rgba(0,0,0,0.7)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 2000
    }}>
      <div style={{
        width: '90%',
        maxWidth: '900px',
        height: '80%',
        backgroundColor: 'white',
        borderRadius: '12px',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        boxShadow: '0 10px 30px rgba(0,0,0,0.3)'
      }}>
        
        {/* Header */}
        <div style={{
          padding: '20px',
          backgroundColor: '#f8f9fa',
          borderBottom: '1px solid #dee2e6',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <div>
            <h3 style={{ margin: 0, fontSize: '20px', color: '#212529' }}>
              📚 Bibliothèque de Ressources
            </h3>
            <p style={{ margin: '5px 0 0 0', fontSize: '14px', color: '#6c757d' }}>
              {resources.length} ressources disponibles • {userType === 'prof' ? 'Mode Professeur - Affichage Auto' : 'Mode Visualisation'}
            </p>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '24px',
              cursor: 'pointer',
              color: '#6c757d',
              padding: '5px'
            }}
          >
            ✕
          </button>
        </div>

        {/* Filtres */}
        <div style={{
          padding: '15px 20px',
          backgroundColor: '#ffffff',
          borderBottom: '1px solid #dee2e6',
          display: 'flex',
          gap: '15px',
          alignItems: 'center',
          flexWrap: 'wrap'
        }}>
          {/* Recherche */}
          <input
            type="text"
            placeholder="🔍 Rechercher une ressource..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{
              flex: '1',
              minWidth: '200px',
              padding: '10px 15px',
              border: '2px solid #e9ecef',
              borderRadius: '8px',
              fontSize: '14px'
            }}
          />
          
          {/* Filtre catégorie */}
          <select
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
            style={{
              padding: '10px 16px',
              border: '2px solid #059669',
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: 600,
              color: '#065f46',
              backgroundColor: '#f0fdf8',
              cursor: 'pointer',
              minWidth: '210px'
            }}
          >
            {categories.map(cat => (
              <option key={cat.id} value={cat.id}>
                {cat.icon} {cat.label}
              </option>
            ))}
          </select>
        </div>

        {/* Contenu */}

        <div style={{ flex: 1, overflow: 'auto', padding: '20px' }}>
          {/* ✨ Bouton Alphabet Interactif */}
          <div 
            onClick={() => setShowAlphabet(true)}
            style={{
              background: 'linear-gradient(135deg, #059669 0%, #047857 100%)',
              borderRadius: '12px',
              padding: '14px 18px',
              marginBottom: '16px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '15px',
              transition: 'transform 0.2s, box-shadow 0.2s'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 8px 22px rgba(5, 150, 105, 0.35)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = 'none';
            }}
          >
            <div style={{ fontSize: '30px' }}>🔤</div>
            <div>
              <div style={{ color: 'white', fontSize: '16px', fontWeight: '700' }}>
                Alphabet Arabe Interactif
              </div>
              <div style={{ color: 'rgba(255,255,255,0.8)', fontSize: '14px', marginTop: '4px' }}>
                28 lettres • Audio • Formes • Exemples
              </div>
            </div>
            <div style={{ marginLeft: 'auto', color: 'white', fontSize: '24px' }}>→</div>
          </div>

          
          {isLoading ? (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: '200px',
              color: '#6c757d'
            }}>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '48px', marginBottom: '10px' }}>⏳</div>
                <div>Chargement de la bibliothèque...</div>
              </div>
            </div>
          ) : filteredResources.length === 0 ? (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: '200px',
              color: '#6c757d',
              textAlign: 'center'
            }}>
              <div>
                <div style={{ fontSize: '48px', marginBottom: '10px' }}>📚</div>
                <div style={{ fontSize: '16px', fontWeight: '600' }}>
                  {searchTerm || selectedCategory !== 'all' ? 
                    'Aucune ressource trouvée' : 
                    'Aucune ressource trouvée'
                  }
                </div>
                <div style={{ fontSize: '14px', marginTop: '8px' }}>
                  {searchTerm || selectedCategory !== 'all' ? 
                    'Essayez de modifier vos filtres' : 
                    'Placez vos fichiers dans le dossier backend/library/'
                  }
                </div>
              </div>
            </div>
          ) : (
            <div style={{
              border: '1px solid #e6e9e4',
              borderRadius: '12px',
              overflow: 'hidden',
              backgroundColor: '#ffffff'
            }}>
              {filteredResources.map((resource) => (
                <div
                  key={resource.id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    padding: '9px 14px',
                    borderBottom: '1px solid #eef1ee',
                    backgroundColor: '#ffffff',
                    transition: 'background-color 0.15s ease',
                    cursor: 'pointer'
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#f4faf7'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#ffffff'; }}
                >
                  {/* En-tête de ressource */}
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    flex: 1,
                    minWidth: 0
                  }}>
                    <div style={{
                      fontSize: '22px',
                      lineHeight: '1',
                      width: '26px',
                      textAlign: 'center',
                      flexShrink: 0
                    }}>
                      {getFileIcon(resource.fileType)}
                    </div>
                    
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{
                        fontSize: '14px',
                        fontWeight: '600',
                        color: '#212529',
                        marginBottom: '4px',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}>
                        {resource.fileName}
                      </div>
                      
                      <div style={{
                        fontSize: '12px',
                        color: '#6c757d',
                        marginBottom: '6px'
                      }}>
                        {resource.categoryLabel} • {formatFileSize(resource.fileSize)}
                      </div>
                      
                      {resource.supportsSync && (
                        <span style={{ fontSize: '10px', color: '#059669', fontWeight: 600 }}>
                          ● Synchronisé
                        </span>
                      )}
                    </div>
                  </div>
                  
                  {/* Actions */}
                  <div style={{ display: 'flex', gap: '8px', flexShrink: 0 }}>
                    {userType === 'prof' && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          shareResource(resource);
                        }}
                        style={{
                          flexShrink: 0,
                          padding: '7px 16px',
                          backgroundColor: '#059669',
                          color: 'white',
                          border: 'none',
                          borderRadius: '8px',
                          cursor: 'pointer',
                          fontSize: '12px',
                          fontWeight: '600',
                          transition: 'background-color 0.2s'
                        }}
                        onMouseEnter={(e) => e.target.style.backgroundColor = '#047857'}
                        onMouseLeave={(e) => e.target.style.backgroundColor = '#059669'}
                      >
                        Afficher
                      </button>
                    )}
                    
                    {userType !== 'prof' && (
                      <button
                        disabled
                        style={{
                          flexShrink: 0,
                          padding: '7px 14px',
                          backgroundColor: '#eef1ee',
                          color: '#6b7c74',
                          border: 'none',
                          borderRadius: '8px',
                          cursor: 'not-allowed',
                          fontSize: '12px',
                          fontWeight: '600'
                        }}
                      >
                        Lecture seule
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{
          padding: '12px 20px',
          backgroundColor: '#ecfdf5',
          borderTop: '1px solid #d1fae5',
          fontSize: '12px',
          color: '#047857',
          textAlign: 'center'
        }}>
          🔒 Ressources protégées • Streaming seulement • 
          {userType === 'prof' ? ' 🚀 Affichage Automatique Activé (Phase 1)' : ' Mode Visualisation Seule'}
        </div>
      </div>
       {/* ✨ Modal Alphabet Interactif */}
       {showAlphabet && (
  <LetterLibrary 
    onClose={() => setShowAlphabet(false)}
    client={client}
    channelName={channelName}
    userType={userType}
  />
)} 
    </div>
  );
};

export default LibraryPanel;
